import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { isAdmin, isParent, can } from "@/lib/roles";

const DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];
const blankWeek = Object.fromEntries(DAYS.map(([d]) => [d, { on: !["sat", "sun"].includes(d), arrive: "08:00", pickup: "17:30" }]));

export const ClassroomTab = ({ student, user, onChanged }) => {
  const [meta, setMeta] = useState({ rooms: [], teachers: [] });
  const [hist, setHist] = useState(null);
  const [sched, setSched] = useState(student.schedule || blankWeek);
  const [effective, setEffective] = useState("");
  const [move, setMove] = useState({ room: student.room, effective_date: "", reason: "" });
  const [teach, setTeach] = useState({ primary_teacher_email: student.teachers.find((t) => t.primary && !t.from_room)?.email || "", teacher_emails: student.teachers.filter((t) => !t.primary && !t.from_room).map((t) => t.email) });

  const load = useCallback(async () => {
    try {
      const [m, h] = await Promise.all([api.get("/students/meta"), api.get(`/students/${student.id}/classroom-history`)]);
      setMeta(m.data); setHist(h.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [student.id]);
  useEffect(() => { load(); }, [load]);

  const room = meta.rooms.find((r) => r.room === student.room);
  const editable = !isParent(user) && can(user, "students", "edit");

  const saveSchedule = async () => {
    try { await api.put(`/students/${student.id}/schedule`, { schedule: sched, effective_date: effective }); toast.success("Weekly schedule saved"); onChanged(); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const transfer = async () => {
    if (move.reason.trim().length < 2) return toast.error("Please record a reason for the classroom change");
    try {
      const { data } = await api.post(`/students/${student.id}/transfer`, move);
      toast.success(data.scheduled ? `Transfer scheduled for ${data.scheduled.effective_date}` : `Moved to ${data.room}`);
      setMove({ ...move, reason: "" }); onChanged(); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const cancelTransfer = async () => {
    try { await api.delete(`/students/${student.id}/transfer`); toast.success("Scheduled transfer cancelled"); onChanged(); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveTeachers = async () => {
    try { await api.put(`/students/${student.id}/teachers`, teach); toast.success("Teachers updated"); onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="classroom-tab">
      <div className="bg-white rounded-2xl border border-border p-6" data-testid="classroom-info">
        <h2 className="font-display text-lg font-semibold">Current classroom</h2>
        <p className="mt-2 font-display text-3xl font-bold text-[#3B82F6]">{student.room}</p>
        <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
          {[["Age group", room?.age_group || student.age_group || "—"], ["Program", room?.program || student.program],
            ["Capacity", room?.capacity ?? "—"], ["Max ratio", room?.max_ratio ? `1:${room.max_ratio}` : "—"],
            ["Primary teacher", student.teachers.find((t) => t.primary)?.name || "Not assigned"],
            ["Supporting teachers", student.teachers.filter((t) => !t.primary).map((t) => t.name).join(", ") || "—"],
            ["Meal schedule", room?.meal_schedule || "—"], ["Nap schedule", room?.nap_schedule || "—"],
            ["Schedule effective", student.schedule_effective || "—"]].map(([k, v]) => (
            <div key={k}><dt className="text-muted-foreground font-semibold">{k}</dt><dd className="font-bold">{v}</dd></div>
          ))}
        </dl>
        {room?.activities && <p className="mt-4 text-sm"><span className="font-bold">Scheduled activities:</span> {room.activities}</p>}
        {student.scheduled_transfer && (
          <div className="mt-4 rounded-xl bg-blue-50 p-3 text-sm flex justify-between items-center gap-2" data-testid="scheduled-transfer">
            <span>Moves to <strong>{student.scheduled_transfer.room}</strong> on {student.scheduled_transfer.effective_date} — {student.scheduled_transfer.reason}</span>
            {isAdmin(user) && <Button size="sm" variant="ghost" className="rounded-full" onClick={cancelTransfer} data-testid="cancel-transfer-btn">Cancel</Button>}
          </div>
        )}
        {isAdmin(user) && (
          <div className="mt-6 border-t border-border pt-4 space-y-3" data-testid="transfer-form">
            <h3 className="font-bold">Move to another classroom</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold">Classroom</Label>
                <Select value={move.room} onValueChange={(v) => setMove({ ...move, room: v })}>
                  <SelectTrigger className="mt-1 h-10 rounded-xl" data-testid="transfer-room"><SelectValue /></SelectTrigger>
                  <SelectContent>{meta.rooms.map((r) => <SelectItem key={r.room} value={r.room}>{r.room}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs font-bold">Effective date (blank = today)</Label><Input type="date" value={move.effective_date} onChange={(e) => setMove({ ...move, effective_date: e.target.value })} className="mt-1 h-10 rounded-xl" data-testid="transfer-date" /></div>
              <div className="sm:col-span-2"><Label className="text-xs font-bold">Reason <span className="text-[#EF4444]">*</span></Label><Input value={move.reason} onChange={(e) => setMove({ ...move, reason: e.target.value })} className="mt-1 h-10 rounded-xl" placeholder="Age transition, family request…" data-testid="transfer-reason" /></div>
            </div>
            <Button onClick={transfer} disabled={move.room === student.room && !move.effective_date} className="rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="transfer-btn">{move.effective_date && move.effective_date > new Date().toISOString().slice(0, 10) ? "Schedule transfer" : "Move now"}</Button>
          </div>
        )}
        {isAdmin(user) && (
          <div className="mt-6 border-t border-border pt-4 space-y-3" data-testid="teachers-form">
            <h3 className="font-bold">Assigned teachers</h3>
            <div><Label className="text-xs font-bold">Primary teacher</Label>
              <Select value={teach.primary_teacher_email || "none"} onValueChange={(v) => setTeach({ ...teach, primary_teacher_email: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1 h-10 rounded-xl" data-testid="primary-teacher"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">Use classroom's teacher</SelectItem>{meta.teachers.map((t) => <SelectItem key={t.email} value={t.email}>{t.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="flex flex-wrap gap-2">
              {meta.teachers.filter((t) => t.email !== teach.primary_teacher_email).map((t) => (
                <label key={t.email} className="text-xs font-semibold flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border">
                  <input type="checkbox" checked={teach.teacher_emails.includes(t.email)} onChange={(e) => setTeach({ ...teach, teacher_emails: e.target.checked ? [...teach.teacher_emails, t.email] : teach.teacher_emails.filter((x) => x !== t.email) })} data-testid={`support-teacher-${t.email}`} /> {t.name}
                </label>
              ))}
            </div>
            <Button onClick={saveTeachers} variant="outline" className="rounded-full font-bold" data-testid="save-teachers-btn">Save teachers</Button>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div className="bg-white rounded-2xl border border-border p-6" data-testid="schedule-editor">
          <h2 className="font-display text-lg font-semibold">Weekly schedule</h2>
          <p className="text-xs text-muted-foreground mt-1">Expected arrival and pickup drive late-arrival and late-pickup minutes.</p>
          <div className="mt-4 space-y-2">
            {DAYS.map(([d, label]) => (
              <div key={d} className="flex items-center gap-3 text-sm" data-testid={`schedule-row-${d}`}>
                <label className="w-28 font-bold flex items-center gap-2">
                  <input type="checkbox" disabled={!editable} checked={Boolean(sched[d]?.on)} onChange={(e) => setSched({ ...sched, [d]: { ...(sched[d] || blankWeek[d]), on: e.target.checked } })} data-testid={`sched-on-${d}`} /> {label}
                </label>
                <Input type="time" disabled={!editable || !sched[d]?.on} value={sched[d]?.arrive || "08:00"} onChange={(e) => setSched({ ...sched, [d]: { ...sched[d], arrive: e.target.value } })} className="h-9 rounded-xl w-32" data-testid={`sched-arrive-${d}`} />
                <span className="text-muted-foreground">to</span>
                <Input type="time" disabled={!editable || !sched[d]?.on} value={sched[d]?.pickup || "17:30"} onChange={(e) => setSched({ ...sched, [d]: { ...sched[d], pickup: e.target.value } })} className="h-9 rounded-xl w-32" data-testid={`sched-pickup-${d}`} />
              </div>
            ))}
          </div>
          {editable && (
            <div className="mt-4 flex flex-wrap gap-3 items-end">
              <div><Label className="text-xs font-bold">Effective from</Label><Input type="date" value={effective} onChange={(e) => setEffective(e.target.value)} className="mt-1 h-10 rounded-xl w-44" data-testid="schedule-effective" /></div>
              <Button onClick={saveSchedule} className="rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="save-schedule-btn">Save schedule</Button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-border p-6" data-testid="classroom-history">
          <h2 className="font-display text-lg font-semibold">Classroom history</h2>
          {!hist && <p className="mt-3 text-sm text-muted-foreground">Loading…</p>}
          {hist?.history.length === 0 && <p className="mt-3 text-sm text-muted-foreground">No classroom changes recorded.</p>}
          <ul className="mt-3 space-y-2 text-sm">
            {hist?.history.map((h) => (
              <li key={h.id} className="flex flex-wrap justify-between gap-2 border-b border-border pb-2 last:border-0">
                <span><strong>{h.from_room || "New enrollment"}</strong> → <strong>{h.to_room}</strong>{h.reason ? ` · ${h.reason}` : ""}</span>
                <span className="text-muted-foreground font-semibold">{h.effective_date} · {h.changed_by}</span>
              </li>
            ))}
          </ul>
          {hist?.schedule_history?.length > 0 && (
            <>
              <h3 className="mt-5 font-bold text-sm">Previous schedules</h3>
              <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                {hist.schedule_history.map((h, i) => <li key={`${h.ended}-${i}`}>Effective {h.effective || "—"} until {h.ended} · changed by {h.changed_by}</li>)}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
